const AW_ITEM_SELECTOR = '[data-aw-wheel-item]';
const AW_META_REVEAL_DELAY = 160;
const AW_PRICE_REVEAL_DELAY = 330;

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

class AntlitzwerkProductWheel extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    this.viewport = this.querySelector('[data-aw-wheel-viewport]');
    this.track = this.querySelector('[data-aw-wheel-track]');
    this.typeElement = this.querySelector('[data-aw-wheel-type]');
    this.liveRegion = this.querySelector('[data-aw-wheel-live]');
    this.priceElement = this.nextElementSibling instanceof HTMLElement
      && this.nextElementSibling.tagName.toLowerCase() === 'product-price'
      ? this.nextElementSibling
      : null;

    if (!(this.viewport instanceof HTMLElement) || !(this.track instanceof HTMLElement)) return;

    this.abortController = new AbortController();
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.currentHandle = this.dataset.awCurrentHandle || '';
    this.focusedHandle = this.currentHandle;
    this.pointerState = null;
    this.suppressClickUntil = 0;
    this.scrollFrame = 0;
    this.settleTimer = 0;
    this.metaTimer = 0;
    this.priceTimer = 0;
    this.resizeFrame = 0;
    this.animationFrame = 0;
    this.animationToken = 0;
    this.ignoreScrollUntil = 0;
    this.lastViewportWidth = 0;
    this.isPositioned = false;
    this.isAnimating = false;
    this.isRebasing = false;

    /* Liquid already supplies the complete first visible frame. Keep it in
       place while the live loop is prepared invisibly, then swap layers only
       after the current product already has its final geometry and styling. */
    this.classList.remove('is-aw-ready');
    this.prepareItems();
    this.bindEvents();
    this.positionInitialItem();
  }

  disconnectedCallback() {
    this._connected = false;
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
    cancelAnimationFrame(this.resizeFrame);
    this.cancelScrollAnimation();
    window.clearTimeout(this.settleTimer);
    this.clearRevealTimers();
  }

  prepareItems() {
    this.track.querySelectorAll(`${AW_ITEM_SELECTOR}:not([data-aw-copy='0'])`).forEach((item) => item.remove());

    this.originalItems = [...this.track.querySelectorAll(`${AW_ITEM_SELECTOR}[data-aw-copy='0']`)];
    this.originalItems.forEach((item, index) => {
      item.dataset.awIndex = String(index);
      item.tabIndex = -1;
    });

    if (this.originalItems.length > 1) {
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();

      this.originalItems.forEach((item) => before.append(this.cloneItem(item, '-1')));
      this.originalItems.forEach((item) => after.append(this.cloneItem(item, '1')));

      this.track.prepend(before);
      this.track.append(after);
    }

    this.items = [...this.track.querySelectorAll(AW_ITEM_SELECTOR)];
  }

  cloneItem(item, copy) {
    const clone = item.cloneNode(true);
    clone.dataset.awCopy = copy;
    clone.removeAttribute('id');
    clone.removeAttribute('aria-current');
    clone.setAttribute('aria-hidden', 'true');
    clone.tabIndex = -1;
    return clone;
  }

  bindEvents() {
    const { signal } = this.abortController;
    const interactionTargets = [this];
    if (this.priceElement) interactionTargets.push(this.priceElement);

    this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    interactionTargets.forEach((target) => {
      target.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      target.addEventListener('pointermove', (event) => this.onPointerMove(event), { passive: false, signal });
      target.addEventListener('pointerup', (event) => this.onPointerEnd(event), { signal });
      target.addEventListener('pointercancel', (event) => this.onPointerEnd(event), { signal });
      target.addEventListener('mousedown', (event) => this.onMouseDown(event), { signal });
      target.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      target.addEventListener('click', (event) => this.onClick(event), { signal });
    });
    this.viewport.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
    window.addEventListener('mousemove', (event) => this.onMouseMove(event), { passive: false, signal });
    window.addEventListener('mouseup', (event) => this.onMouseUp(event), { signal });
    window.addEventListener('blur', () => this.onWindowBlur(), { signal });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.onResize());
      this.resizeObserver.observe(this.viewport);
    } else {
      window.addEventListener('resize', () => this.onResize(), { passive: true, signal });
    }
  }

  positionInitialItem() {
    if (!this._connected || !this.originalItems.length || this.isPositioned) return;

    const current = this.originalItems.find((item) => item.dataset.awCurrent === 'true') || this.originalItems[0];
    if (!current) return;

    /* Visibility:hidden keeps the live layer measurable. If a Theme Editor
       parent is temporarily display:none, leave the complete server-rendered
       frame untouched; ResizeObserver will initialise the live layer as soon as
       real geometry exists, without a frame-by-frame retry loop. */
    if (this.viewport.clientWidth <= 0 || current.offsetWidth <= 0) return;

    this.jumpToItem(current);
    this.setFocusedItem(current, { immediate: true, announce: false });
    this.updateVisualState();

    this.lastViewportWidth = this.viewport.clientWidth;
    this.isPositioned = true;
    this.classList.add('is-aw-ready');
  }

  onResize() {
    if (!this.isPositioned) {
      this.positionInitialItem();
      return;
    }

    const nextWidth = this.viewport.clientWidth;
    if (Math.abs(nextWidth - this.lastViewportWidth) < 0.5) return;
    this.lastViewportWidth = nextWidth;
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => {
      this.cancelScrollAnimation();
      const target = this.originalItems.find((item) => item.dataset.awHandle === this.focusedHandle)
        || this.originalItems.find((item) => item.dataset.awCurrent === 'true')
        || this.originalItems[0];
      if (!target) return;
      this.jumpToItem(target);
      this.updateVisualState();
    });
  }

  onScroll() {
    if (!this.isPositioned) return;

    this.scheduleVisualUpdate();
    if (this.isRebasing || this.isAnimating || performance.now() < this.ignoreScrollUntil) return;
    if (this.pointerState?.axis === 'horizontal') return;

    this.hideMetaAndPrice();
    window.clearTimeout(this.settleTimer);
    this.settleTimer = window.setTimeout(() => this.settleToNearest({ source: 'native' }), 145);
  }

  scheduleVisualUpdate() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.updateVisualState();
    });
  }

  updateVisualState() {
    if (!this.viewport || !this.items?.length) return;

    const viewportCenter = this.viewport.scrollLeft + (this.viewport.clientWidth / 2);
    let nearest = null;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.items.forEach((item) => {
      const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
      const distance = Math.abs(itemCenter - viewportCenter);
      const span = Math.max(item.offsetWidth * 1.12, 1);
      const proximity = clamp(1 - (distance / span), 0, 1);
      const eased = proximity * proximity * (3 - (2 * proximity));
      const isCurrent = item.dataset.awCurrent === 'true';
      const sidePullLimit = this.viewport.clientWidth < 600 ? 26 : 34;
      const isWithinNeighbourRange = distance <= item.offsetWidth * 1.42;
      const sidePull = isWithinNeighbourRange
        ? Math.sign(viewportCenter - itemCenter) * Math.min(sidePullLimit, distance * 0.12)
        : 0;

      let colorAlpha;
      let scale;
      let glowTightAlpha = 0;
      let glowAlpha = 0;
      let glowSoftAlpha = 0;
      let markerAlpha = 0;
      let markerGlowAlpha = 0;
      let markerScale = 0.35;

      if (isCurrent) {
        colorAlpha = 0.38 + (0.62 * eased);
        scale = 0.91 + (0.09 * eased);

        const glowProgressRaw = clamp((proximity - 0.1) / 0.9, 0, 1);
        const glowProgress = glowProgressRaw * glowProgressRaw * (3 - (2 * glowProgressRaw));
        glowTightAlpha = 0.055 + (0.335 * glowProgress);
        glowAlpha = 0.04 + (0.295 * glowProgress);
        glowSoftAlpha = 0.018 + (0.145 * glowProgress);

        const markerProgressRaw = clamp((proximity - 0.13) / 0.87, 0, 1);
        const markerProgress = markerProgressRaw * markerProgressRaw * (3 - (2 * markerProgressRaw));
        markerAlpha = 0.055 + (0.77 * markerProgress);
        markerGlowAlpha = 0.018 + (0.23 * markerProgress);
        markerScale = 0.38 + (0.62 * markerProgress);
      } else {
        colorAlpha = 0.28 + (0.39 * eased);
        scale = 0.9 + (0.075 * eased);
      }

      this.setItemVariable(item, '--aw-item-color-alpha', colorAlpha.toFixed(3));
      this.setItemVariable(item, '--aw-item-scale', scale.toFixed(4));
      this.setItemVariable(item, '--aw-item-glow-tight-alpha', glowTightAlpha.toFixed(3));
      this.setItemVariable(item, '--aw-item-glow-alpha', glowAlpha.toFixed(3));
      this.setItemVariable(item, '--aw-item-glow-soft-alpha', glowSoftAlpha.toFixed(3));
      this.setItemVariable(item, '--aw-item-marker-alpha', markerAlpha.toFixed(3));
      this.setItemVariable(item, '--aw-item-marker-glow-alpha', markerGlowAlpha.toFixed(3));
      this.setItemVariable(item, '--aw-item-marker-scale', markerScale.toFixed(3));
      this.setItemVariable(item, '--aw-item-shift', `${sidePull.toFixed(2)}px`);
      item.classList.toggle('is-aw-near-center', isCurrent && proximity >= 0.34);
      item.classList.toggle(
        'is-aw-visible',
        distance <= (this.viewport.clientWidth / 2) + (item.offsetWidth * 0.9)
      );

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = item;
      }
    });

    this.items.forEach((item) => item.classList.toggle('is-aw-focused', item === nearest));
    this.nearestItem = nearest;
  }

  setItemVariable(item, property, value) {
    const cache = item._awStyleCache || (item._awStyleCache = Object.create(null));
    if (cache[property] === value) return;
    cache[property] = value;
    item.style.setProperty(property, value);
  }

  clearRevealTimers() {
    window.clearTimeout(this.metaTimer);
    window.clearTimeout(this.priceTimer);
    this.metaTimer = 0;
    this.priceTimer = 0;
  }

  hideMetaAndPrice() {
    this.clearRevealTimers();
    this.dataset.awMetaVisible = 'false';
    this.dataset.awPriceVisible = 'false';
    this.classList.add('is-aw-interacting');
  }

  settleToNearest(options = {}) {
    if (!this.isPositioned || !this.items.length) return;

    window.clearTimeout(this.settleTimer);
    this.updateVisualState();

    const target = options.targetItem || this.nearestItem || this.findNearestItem();
    if (!target) return;

    const targetLeft = this.leftForItem(target);
    const distance = Math.abs(this.viewport.scrollLeft - targetLeft);
    const shouldAnimate = options.animate !== false && !this.reducedMotionQuery.matches;

    if (distance > 0.75 && shouldAnimate) {
      this.animateScrollTo(targetLeft, {
        targetItem: target,
        velocity: Number.isFinite(options.velocity) ? options.velocity : 0,
        source: options.source || 'settle',
      });
      return;
    }

    this.ignoreScrollUntil = performance.now() + 90;
    this.viewport.scrollLeft = targetLeft;
    this.finalizeSettle(target);
  }

  animateScrollTo(targetLeft, { targetItem, velocity = 0, source = 'settle' } = {}) {
    this.cancelScrollAnimation();

    const startLeft = this.viewport.scrollLeft;
    const distance = targetLeft - startLeft;
    if (Math.abs(distance) <= 0.75 || this.reducedMotionQuery.matches) {
      this.viewport.scrollLeft = targetLeft;
      this.finalizeSettle(targetItem || this.findNearestItem());
      return;
    }

    const speed = Math.abs(velocity);
    const sourceDelay = source === 'click' ? 35 : source === 'key' ? 20 : 0;
    const duration = clamp(
      370 + (Math.abs(distance) * 0.22) - Math.min(speed * 38, 62) + sourceDelay,
      330,
      535
    );

    const directionalVelocity = Math.sign(velocity) === Math.sign(distance) ? velocity : 0;
    const initialSlopeRatio = clamp((directionalVelocity * duration) / distance, 0, 2.15);
    const initialTangent = distance * initialSlopeRatio;
    const startedAt = performance.now();
    const token = ++this.animationToken;

    this.isAnimating = true;
    this.classList.add('is-aw-settling');

    const step = (now) => {
      if (!this._connected || token !== this.animationToken) return;

      const progress = clamp((now - startedAt) / duration, 0, 1);
      const t2 = progress * progress;
      const t3 = t2 * progress;
      const h00 = (2 * t3) - (3 * t2) + 1;
      const h10 = t3 - (2 * t2) + progress;
      const h01 = (-2 * t3) + (3 * t2);
      const position = (h00 * startLeft) + (h10 * initialTangent) + (h01 * targetLeft);

      this.viewport.scrollLeft = position;
      this.updateVisualState();

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(step);
        return;
      }

      this.animationFrame = 0;
      this.viewport.scrollLeft = targetLeft;
      this.isAnimating = false;
      this.classList.remove('is-aw-settling');
      this.ignoreScrollUntil = performance.now() + 90;
      this.finalizeSettle(targetItem || this.findNearestItem());
    };

    this.animationFrame = requestAnimationFrame(step);
  }

  cancelScrollAnimation() {
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = 0;
    this.animationToken += 1;
    this.isAnimating = false;
    this.classList.remove('is-aw-settling');
  }

  finalizeSettle(item) {
    if (!item) return;

    this.rebaseIfNeeded(item);
    const focused = this.originalItems.find((candidate) => candidate.dataset.awHandle === item.dataset.awHandle) || item;
    this.setFocusedItem(focused, { immediate: false, announce: true });
    this.classList.remove('is-aw-interacting');
    this.updateVisualState();
  }

  rebaseIfNeeded(item) {
    if (item.dataset.awCopy === '0' || this.originalItems.length < 2) return;
    const original = this.originalItems.find((candidate) => candidate.dataset.awHandle === item.dataset.awHandle);
    if (!original) return;
    this.jumpToItem(original);
    this.nearestItem = original;
  }

  setFocusedItem(item, { immediate = false, announce = true } = {}) {
    if (!item) return;

    this.focusedHandle = item.dataset.awHandle || this.currentHandle;
    const isCurrent = this.focusedHandle === this.currentHandle;
    const name = item.dataset.awName || '';
    const type = item.dataset.awType || '';

    this.clearRevealTimers();
    this.dataset.awFocusCurrent = String(isCurrent);
    this.dataset.awMetaVisible = immediate ? 'true' : 'false';
    this.dataset.awPriceVisible = immediate ? String(isCurrent) : 'false';

    if (this.typeElement) {
      this.typeElement.hidden = !type;
      this.typeElement.replaceChildren();
      if (type) {
        const dash = document.createElement('span');
        dash.setAttribute('aria-hidden', 'true');
        dash.textContent = '— ';
        this.typeElement.append(dash, document.createTextNode(type));
      }
    }

    if (!immediate && !this.reducedMotionQuery.matches) {
      this.metaTimer = window.setTimeout(() => {
        this.metaTimer = 0;
        this.dataset.awMetaVisible = 'true';
      }, AW_META_REVEAL_DELAY);

      if (isCurrent) {
        this.priceTimer = window.setTimeout(() => {
          this.priceTimer = 0;
          this.dataset.awPriceVisible = 'true';
        }, AW_PRICE_REVEAL_DELAY);
      }
    } else {
      this.dataset.awMetaVisible = 'true';
      this.dataset.awPriceVisible = String(isCurrent);
    }

    if (announce && this.liveRegion) {
      const state = isCurrent ? this.dataset.awLabelCurrent : this.dataset.awLabelPreview;
      this.liveRegion.textContent = [name, type, state].filter(Boolean).join('. ');
    }
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' || event.isPrimary === false || event.button !== 0) return;
    const captureElement = event.currentTarget instanceof HTMLElement ? event.currentTarget : this;
    this.beginDrag(`pointer:${event.pointerId}`, event.clientX, event.clientY, captureElement);
  }

  onPointerMove(event) {
    const state = this.pointerState;
    if (!state || state.id !== `pointer:${event.pointerId}`) return;

    const claimed = this.moveDrag(event.clientX, event.clientY);
    if (!claimed) return;

    event.preventDefault();
    if (!state.captured) {
      try {
        if (typeof state.captureElement?.setPointerCapture === 'function') {
          state.captureElement.setPointerCapture(event.pointerId);
          state.captured = true;
        }
      } catch (_) {
        // Capture can fail if the browser has already committed to vertical page scrolling.
      }
    }
  }

  onPointerEnd(event) {
    const state = this.pointerState;
    if (!state || state.id !== `pointer:${event.pointerId}`) return;
    this.endDrag(event);
  }

  onMouseDown(event) {
    if (event.button !== 0) return;
    const captureElement = event.currentTarget instanceof HTMLElement ? event.currentTarget : this;
    this.beginDrag('mouse', event.clientX, event.clientY, captureElement);
  }

  onMouseMove(event) {
    const state = this.pointerState;
    if (!state || state.id !== 'mouse') return;
    if (this.moveDrag(event.clientX, event.clientY)) event.preventDefault();
  }

  onMouseUp(event) {
    const state = this.pointerState;
    if (!state || state.id !== 'mouse') return;
    this.endDrag(event);
  }

  onWindowBlur() {
    if (!this.pointerState) return;
    this.endDrag();
  }

  beginDrag(id, clientX, clientY, captureElement = this) {
    if (this.originalItems.length < 2 || this.pointerState) return;

    this.cancelScrollAnimation();
    window.clearTimeout(this.settleTimer);

    const now = performance.now();
    this.pointerState = {
      id,
      captureElement,
      startX: clientX,
      startY: clientY,
      startScrollLeft: this.viewport.scrollLeft,
      axis: null,
      moved: false,
      captured: false,
      samples: [{ time: now, scrollLeft: this.viewport.scrollLeft }],
    };
  }

  moveDrag(clientX, clientY) {
    const state = this.pointerState;
    if (!state) return false;

    const deltaX = clientX - state.startX;
    const deltaY = clientY - state.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (state.axis === null) {
      if (absX < 6 && absY < 6) return false;

      if (absY >= absX * 0.92) {
        state.axis = 'vertical';
        return false;
      }

      state.axis = 'horizontal';
      this.classList.add('is-aw-dragging');
      this.hideMetaAndPrice();
    }

    if (state.axis !== 'horizontal') return false;

    state.moved = state.moved || absX > 5;
    this.viewport.scrollLeft = state.startScrollLeft - deltaX;
    this.recordDragSample(state);
    this.updateVisualState();
    return true;
  }

  recordDragSample(state) {
    const now = performance.now();
    state.samples.push({ time: now, scrollLeft: this.viewport.scrollLeft });
    const cutoff = now - 110;
    while (state.samples.length > 2 && state.samples[0].time < cutoff) state.samples.shift();
  }

  dragVelocity(state) {
    if (!state?.samples?.length) return 0;

    this.recordDragSample(state);
    const samples = state.samples;
    const latest = samples.at(-1);
    const previous = samples.at(-2);
    let earliest = samples[0];

    for (const sample of samples) {
      if (latest.time - sample.time <= 92) {
        earliest = sample;
        break;
      }
    }

    const elapsed = latest.time - earliest.time;
    if (!previous || elapsed < 16 || latest.time - previous.time > 74) return 0;
    return clamp((latest.scrollLeft - earliest.scrollLeft) / elapsed, -2.2, 2.2);
  }

  endDrag(event) {
    const state = this.pointerState;
    if (!state) return;

    this.pointerState = null;
    this.classList.remove('is-aw-dragging');

    if (state.captured && event?.pointerId != null) {
      try {
        if (typeof state.captureElement?.releasePointerCapture === 'function') {
          state.captureElement.releasePointerCapture(event.pointerId);
        }
      } catch (_) {
        // The browser may already have released capture.
      }
    }

    if (state.axis !== 'horizontal') return;

    event?.preventDefault();
    if (state.moved) this.suppressClickUntil = performance.now() + 380;

    const velocity = this.dragVelocity(state);
    const target = this.projectedTarget(velocity);
    this.settleToNearest({ targetItem: target, velocity, source: 'release' });
  }

  projectedTarget(velocity) {
    const nearest = this.findNearestItem();
    if (!nearest || Math.abs(velocity) < 0.08) return nearest;

    const projectionLimit = Math.min(
      this.viewport.clientWidth * 0.58,
      Math.max(nearest.offsetWidth * 1.28, 140)
    );
    const projectedDistance = clamp(velocity * 175, -projectionLimit, projectionLimit);
    let target = this.findNearestItemAtScroll(this.viewport.scrollLeft + projectedDistance) || nearest;

    if (target === nearest && Math.abs(velocity) >= 0.48) {
      const index = this.items.indexOf(nearest);
      target = this.items[index + Math.sign(velocity)] || target;
    }

    return target;
  }

  onClick(event) {
    const item = event.target.closest(AW_ITEM_SELECTOR);
    if (!(item instanceof HTMLAnchorElement) || !this.contains(item)) return;

    if (performance.now() < this.suppressClickUntil) {
      event.preventDefault();
      return;
    }

    this.updateVisualState();
    const centered = this.nearestItem?.dataset.awHandle === item.dataset.awHandle
      && Math.abs(this.viewport.scrollLeft - this.leftForItem(this.nearestItem)) < 3;

    if (!centered) {
      event.preventDefault();
      this.centerItem(item, 'click');
      return;
    }

    if (item.dataset.awCurrent === 'true') {
      event.preventDefault();
      return;
    }

    this.clearRevealTimers();
    this.dataset.awMetaVisible = 'false';
    this.dataset.awPriceVisible = 'false';
    this.classList.add('is-aw-navigating');
  }

  onKeyDown(event) {
    if (this.originalItems.length < 2) return;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.moveBy(event.key === 'ArrowRight' ? 1 : -1);
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const target = event.key === 'Home' ? this.originalItems[0] : this.originalItems.at(-1);
      if (target) this.centerItem(target, 'key');
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const focused = this.originalItems.find((item) => item.dataset.awHandle === this.focusedHandle);
      if (!focused || focused.dataset.awCurrent === 'true') return;
      window.location.assign(focused.href);
    }
  }

  moveBy(direction) {
    this.updateVisualState();
    const physical = this.nearestItem || this.findNearestItem();
    if (!physical) return;

    const physicalIndex = this.items.indexOf(physical);
    const target = this.items[physicalIndex + direction];
    if (target) this.centerItem(target, 'key');
  }

  centerItem(item, source = 'click') {
    if (!item) return;
    this.hideMetaAndPrice();
    this.settleToNearest({ targetItem: item, velocity: 0, source });
  }

  jumpToItem(item) {
    if (!item) return;
    this.isRebasing = true;
    this.viewport.scrollLeft = this.leftForItem(item);
    // Force the instant rebase to be committed before normal scroll handling resumes.
    void this.viewport.offsetWidth;
    this.ignoreScrollUntil = performance.now() + 80;
    requestAnimationFrame(() => {
      this.isRebasing = false;
    });
  }

  findNearestItem() {
    return this.findNearestItemAtScroll(this.viewport.scrollLeft);
  }

  findNearestItemAtScroll(scrollLeft) {
    const viewportCenter = scrollLeft + (this.viewport.clientWidth / 2);
    let nearest = null;
    let distance = Number.POSITIVE_INFINITY;

    this.items.forEach((item) => {
      const itemDistance = Math.abs((item.offsetLeft + (item.offsetWidth / 2)) - viewportCenter);
      if (itemDistance < distance) {
        distance = itemDistance;
        nearest = item;
      }
    });

    return nearest;
  }

  leftForItem(item) {
    const max = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    const target = item.offsetLeft + (item.offsetWidth / 2) - (this.viewport.clientWidth / 2);
    return clamp(target, 0, max);
  }
}

if (!customElements.get('antlitzwerk-product-wheel')) {
  customElements.define('antlitzwerk-product-wheel', AntlitzwerkProductWheel);
}
